<template>
  <div
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50"
  >
    <!-- Header -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center">
        <div class="flex items-center space-x-3">
          <ChatBubbleLeftRightIcon class="h-10 w-10 text-green-600" />
          <ArrowRightIcon class="h-6 w-6 text-gray-400" />
          <UserIcon class="h-10 w-10 text-blue-600" />
        </div>
      </div>
      <h2
        class="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900"
      >
        {{ t("wechatRebind.title") }}
      </h2>
      <p class="mt-2 text-center text-sm/6 text-gray-600">
        {{ t("wechatRebind.subtitle") }}
      </p>
    </div>

    <!-- Main Form -->
    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
        <!-- WeChat Info Banner -->
        <div class="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div class="flex items-center space-x-3">
            <ChatBubbleLeftRightIcon
              class="h-6 w-6 text-green-600 flex-shrink-0"
            />
            <div>
              <h3 class="text-sm font-medium text-green-800">
                {{ t("wechatRebind.currentWechat") }}
              </h3>
              <p class="mt-1 text-sm text-green-700">
                {{ t("wechatRebind.currentWechatDesc") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Login Form -->
        <form
          @submit.prevent="onFinish(formState)"
          class="space-y-6"
          method="post"
          action="#"
        >
          <!-- 用户名输入框 -->
          <div>
            <label
              for="username"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("common.username") }}
            </label>
            <div class="mt-2">
              <input
                id="username"
                v-model="formState.username"
                name="username"
                type="text"
                autocomplete="username"
                required
                :disabled="loading"
                class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('form.enterUsername')"
              />
            </div>
            <p class="mt-2 text-xs text-gray-500">
              {{ t("wechatRebind.usernameHint") }}
            </p>
          </div>

          <!-- 密码输入框 -->
          <div>
            <label
              for="password"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("common.password") }}
            </label>
            <div class="mt-2 relative">
              <input
                id="password"
                v-model="formState.password"
                :type="passwordInputType"
                name="password"
                autocomplete="current-password"
                required
                :disabled="loading"
                class="block w-full rounded-md bg-white px-3 py-1.5 pr-10 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('form.enterPassword')"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  @click="togglePasswordVisibility"
                  tabindex="-1"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none"
                  :aria-label="
                    showPassword
                      ? t('form.hidePassword')
                      : t('form.showPassword')
                  "
                >
                  <EyeIcon
                    v-if="showPassword"
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                  <EyeSlashIcon v-else class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div>
            <button
              type="submit"
              :disabled="loading"
              class="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <a-spin v-if="loading" class="mr-2" size="small" />
              {{
                loading
                  ? t("wechatRebind.processing")
                  : t("wechatRebind.confirm")
              }}
            </button>
          </div>
        </form>

        <!-- Divider -->
        <div class="relative mt-10">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200" />
          </div>
          <div class="relative flex justify-center text-sm/6 font-medium">
            <span class="bg-white px-6 text-gray-900">{{
              t("wechatRebind.helpTitle")
            }}</span>
          </div>
        </div>

        <!-- Help Information -->
        <div class="mt-6">
          <div class="p-4 bg-gray-50 rounded-lg">
            <div class="text-xs text-gray-600 leading-relaxed space-y-4">
              <!-- 绑定规则说明 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("wechatRebind.bindingRules") }}
                </h4>
                <p>
                  {{
                    t("wechatRebind.bindingRulesDesc", {
                      systemShortName: brandConfig.systemShortName,
                    })
                  }}
                </p>
              </div>

              <!-- 换绑操作流程 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("wechatRebind.processTitle") }}
                </h4>
                <p>
                  {{
                    t("wechatRebind.processDesc", {
                      systemShortName: brandConfig.systemShortName,
                    })
                  }}
                </p>
                <p class="mt-2">{{ t("wechatRebind.processNote") }}</p>
              </div>

              <!-- 安全防护措施 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("wechatRebind.securityMeasures") }}
                </h4>
                <p>{{ t("wechatRebind.securityMeasuresDesc") }}</p>
              </div>

              <!-- 登录与退出说明 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("wechatRebind.loginLogoutTitle") }}
                </h4>
                <p>{{ t("wechatRebind.loginLogoutDesc") }}</p>
              </div>

              <!-- 常见问题处理 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("wechatRebind.faqTitle") }}
                </h4>
                <div class="space-y-2">
                  <p>
                    <strong>{{ t("wechatRebind.faq.question1") }}:</strong>
                    {{
                      t("wechatRebind.faq.answer1", {
                        systemShortName: brandConfig.systemShortName,
                      })
                    }}
                  </p>
                  <p>
                    <strong>{{ t("wechatRebind.faq.question2") }}:</strong>
                    {{
                      t("wechatRebind.faq.answer2", {
                        systemShortName: brandConfig.systemShortName,
                      })
                    }}
                  </p>
                  <p>
                    <strong>{{ t("wechatRebind.faq.question3") }}:</strong>
                    {{ t("wechatRebind.faq.answer3") }}
                  </p>
                </div>
              </div>

              <!-- 安全提醒 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("wechatRebind.securityReminder") }}
                </h4>
                <p>{{ t("wechatRebind.securityReminderDesc") }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Cancel Button -->
        <div class="mt-6">
          <button
            type="button"
            @click="handleCancel"
            :disabled="loading"
            class="flex w-full justify-center rounded-md bg-gray-200 px-3 py-1.5 text-sm/6 font-semibold text-gray-700 shadow-xs hover:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ t("wechatRebind.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBrandConfig } from "@/utils/brand";
import { notificationService } from "@/services/notificationService";
import * as Sentry from "@sentry/vue";
import { useAuthStore } from "@/stores/auth";
import {
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/vue/24/outline";

const { t } = useI18n();
const brandConfig = useBrandConfig();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const formState = reactive({
  username: "",
  password: "",
});

const loading = ref(false);

// 密码可见性控制
const showPassword = ref(false);
const passwordInputType = computed(() =>
  showPassword.value ? "text" : "password",
);

function togglePasswordVisibility() {
  showPassword.value = !showPassword.value;
}

// 处理表单提交
async function onFinish(values: any) {
  try {
    loading.value = true;

    Sentry.addBreadcrumb({
      category: "wechat_rebind",
      message: "开始微信换绑登录",
      level: "info",
      data: {
        username: values.username,
      },
    });

    // 登录新账号
    const response = await authStore.handleLogin(
      values.username,
      values.password,
    );

    if (response?.status === 520) {
      notificationService.info("auth.setEmailFirst");
      await router.push({
        path: "/email",
        query: {
          redirect: route.query.redirect,
          ...route.query,
        },
      });
      return;
    }

    notificationService.success("wechatRebind.loginSuccess");

    // 完成微信换绑
    try {
      await authStore.completeWechatRebinding();
      notificationService.success("wechatRebind.rebindSuccess");

      // 换绑成功，跳转到目标系统或默认页面
      const redirectUrl = route.query.redirect as string;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        router.push("/");
      }
    } catch (error: any) {
      console.error("微信换绑失败:", error);

      // 如果是频率限制错误，提供更友好的提示
      if (
        error.message?.includes("频繁") ||
        error.message?.includes("冷却") ||
        error.response?.status === 400 ||
        error.response?.status === 429
      ) {
        const confirmed = await notificationService.confirm(
          "wechatRebind.rebindError",
          "wechatRebind.frequencyError",
        );

        if (confirmed) {
          // 取消换绑，恢复原账号
          authStore.cancelWechatRebinding();
          notificationService.info("wechatRebind.backToOriginalSuccess");
          router.push("/");
          return;
        }
      } else if (error.response?.status === 423) {
        // 专门处理冷却期错误
        notificationService.error("wechatRebind.cooldownError");
      } else {
        notificationService.error(error.message || "wechatRebind.rebindError");
      }
    }
  } catch (error: any) {
    console.error("登录失败:", error);

    if (error.response?.status === 401) {
      notificationService.error("common.loginFailed");
    } else if (error.response?.status === 429) {
      notificationService.error("common.tooManyRequests");
    } else {
      notificationService.error("common.systemError");
    }
  } finally {
    loading.value = false;
  }
}

// 处理取消操作
async function handleCancel() {
  const confirmed = await notificationService.confirm(
    "wechatRebind.cancelTitle",
    "wechatRebind.cancelDesc",
  );

  if (confirmed) {
    // 取消换绑，恢复原账号
    authStore.cancelWechatRebinding();
    notificationService.info("wechatRebind.backToOriginalSuccess");
    router.push("/");
  }
}
</script>
